<template>
    <oc-text-input
        :value="value"
        v-on="$_listeners"
    />
</template>
<script>
import Cleave from 'Cleave'

export default {
    name: 'oc-cleave',
    props: {
        value: {
            default: null,
            validator(value) {
                return value === null || typeof value === 'string' || value instanceof String || typeof value === 'number'
            }
        },
        // https://github.com/nosir/cleave.js/blob/master/doc/options.md
        // https://github.com/vuejs/vue/issues/4060
        // do not pass it inline
        options: {
            type: Object,
            default: () => ({})
        },
        // Set this prop to true to emit raw instead of masked value
        raw: {
            type: Boolean,
            default: false
        },
        //////////////////////////////////////////////////////////////////

        // Credit card numbers:
        creditCard: {
            type: Boolean,
            default: false
        },
        creditCardStrictMode: {
            type: Boolean,
            default: false
        },
        onCreditCardTypeChanged: {
            type: Function,
            default: () => {}
        },

        // Phone numbers:
        phone: {
            type: Boolean,
            default: false
        },
        phoneRegionCode: {
            type: String,
            default: 'AU'
        },

        // Date:
        date: {
            type: Boolean,
            default: false
        },
        datePattern: {
            type: Array,
            default: () => (['d', 'm', 'Y'])
        },
        dateMin: {
            type: String
        },
        dateMax: {
            type: String
        },

        // Time:
        time: {
            type: Boolean,
            default: false
        },
        timePattern: {
            type: Array,
            default: () => (['h', 'm', 's'])
        },

        // Numerals:
        numeral: {
            type: Boolean,
            default: false
        },
        numeralThousandsGroupStyle: {
            type: String,
            default: 'thousand',
            validator: function (value) {
                return ['thousand', 'lakh', 'wan', 'none'].indexOf(value) !== -1
            }
        },
        numeralIntegerScale: {
            type: Number,
            default: 0
        },
        numeralDecimalScale: {
            type: Number,
            default: 2
        },
        numeralDecimalMark: {
            type: String,
            default: '.'
        },
        numeralPositiveOnly: {
            type: Boolean,
            default: false
        },
        signBeforePrefix: {
            type: Boolean,
            default: false
        },
        tailPrefix: {
            type: Boolean,
            default: false
        },
        stripLeadingZeroes: {
            type: Boolean,
            default: true
        },

        // General config:
        blocks: {
            type: Array,
            default: () => ([])
        },
        delimiter: {
            type: String
        },
        delimiters: {
            type: Array,
            default: () => ([])
        },
        delimiterLazyShow: {
            type: Boolean,
            default: false
        },
        prefix: {
            type: String,
            default: () => null
        },
        noImmediatePrefix: {
            type: Boolean,
            default: false
        },
        rawValueTrimPrefix: {
            type: Boolean,
            default: false
        },
        numericOnly: {
            type: Boolean,
            default: false
        },
        uppercase: {
            type: Boolean,
            default: false
        },
        lowercase: {
            type: Boolean,
            default: false
        },
        swapHiddenInput: {
            type: Boolean,
            default: false
        },
        onValueChanged: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            // cleave.js instance
            cleave: null,
            // callback backup
            onValueChangedFn: null
        }
    },
    mounted() {
        /* istanbul ignore if */
        if (this.cleave) return

        this.cleave = new Cleave(this.$el, this.getOptions(this.options))
    },
    computed: {
        $_listeners() {
            let listeners = Object.assign({}, this.$listeners)

            delete listeners.input

            return listeners
        },
    },
    methods: {
        /**
        * Inject our method in config options
        *
        * @param options Object
        */
        getOptions(options) {

            let propOptions = {
                // Credit card numbers:
                creditCard: this.creditCard,
                creditCardStrictMode: this.creditCardStrictMode,
                onCreditCardTypeChanged: this.onCreditCardTypeChanged,
                // Phone numbers:
                phone: this.phone,
                phoneRegionCode: this.phoneRegionCode,
                // Date:
                date: this.date,
                datePattern: this.datePattern,
                dateMin: this.dateMin,
                dateMax: this.dateMax,
                // Time:
                time: this.time,
                timePattern: this.timePattern,
                // Numerals:
                numeral: this.numeral,
                numeralThousandsGroupStyle: this.numeralThousandsGroupStyle,
                numeralIntegerScale: this.numeralIntegerScale,
                numeralDecimalScale: this.numeralDecimalScale,
                numeralDecimalMark: this.numeralDecimalMark,
                numeralPositiveOnly: this.numeralPositiveOnly,
                signBeforePrefix: this.signBeforePrefix,
                tailPrefix: this.tailPrefix,
                stripLeadingZeroes: this.stripLeadingZeroes,
                // General config:
                blocks: this.blocks,
                delimiter: this.delimiter,
                delimiters: this.delimiters,
                delimiterLazyShow: this.delimiterLazyShow,
                prefix: this.prefix,
                noImmediatePrefix: this.noImmediatePrefix,
                rawValueTrimPrefix: this.rawValueTrimPrefix,
                numericOnly: this.numericOnly,
                uppercase: this.uppercase,
                lowercase: this.lowercase,
                swapHiddenInput: this.swapHiddenInput,
                onValueChanged: this.onValueChanged
            }

            let mergedOptions = Object.assign(propOptions, options)

            // Preserve original callback
            this.onValueChangedFn = mergedOptions.onValueChanged

            mergedOptions.onValueChanged = this.onValueChangedWrapper

            return mergedOptions
        },
        /**
        * Watch for value changed by cleave and notify parent component
        *
        * @param event
        */
        onValueChangedWrapper(event) {
            let value = this.raw ? event.target.rawValue : event.target.value
            this.$emit('input', value)

            // Call original callback method
            if (typeof this.onValueChangedFn === 'function') {
                this.onValueChangedFn.call(this, event)
            }
        }
    },
    watch: {
        /**
        * Watch for any changes in options and redraw
        *
        * @param newOptions Object
        */
        options: {
            deep: true,
            handler(newOptions) {
                this.cleave.destroy()
                this.cleave = new Cleave(this.$el, this.getOptions(newOptions))
                this.cleave.setRawValue(this.value)
            }
        },

        /**
        * Watch for changes from parent component and notify cleave instance
        *
        * @param newValue
        */
        value(newValue) {
            /* istanbul ignore if */
            if (!this.cleave) return

            // when v-model is not masked (raw)
            if (this.raw && newValue === this.cleave.getRawValue()) return
            //  when v-model is masked (NOT raw)
            if (!this.raw && newValue === this.$el.value) return
            // Lastly set newValue
            this.cleave.setRawValue(newValue)
        }
    },
    /**
    * Free up memory
    */
    beforeDestroy() {
        /* istanbul ignore if */
        if (!this.cleave) return

        this.cleave.destroy()
        this.cleave = null
        this.onValueChangedFn = null
    }
}
</script>
